import React, { useState } from 'react';
import { Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AlTextField } from '../../../common/components/AlTextField';
import { AuthService } from '../../../api/AuthService';
import { findLang } from '../../../common/models/lang';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import { ConsentNote } from '../ConsentNote/ConsentNote';
import { FormActions } from '../FormActions/FormActions';
import { logAmplitudeEvent } from '../../../common/services/amplitudeService';

// Same plausibility regex as the backend (legacy-activation.js VALID_EMAIL).
const VALID_EMAIL = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

const SignupForm = ({ onCancel, onLogin, notifications, prefillEmail = '' }) => {
    const { t, i18n } = useTranslation();
    const { languages = [] } = useDictionary();
    const [busy, setBusy] = useState(false);
    const [email, setEmail] = useState(prefillEmail);
    const [touched, setTouched] = useState(false);
    const isValid = VALID_EMAIL.test(email.trim());

    const onSubmit = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setBusy(true);
        notifications.clearError();
        const language = languages.find(({ name }) => name === findLang(i18n.language).name);
        AuthService.signup({ email: email.trim(), languageId: language ? language.id : undefined })
            .then(() => {
                // The endpoint answers the same for known emails (enumeration contract), so this
                // counts requests — 'Signup: Completed' on the ResetPage counts real new accounts.
                logAmplitudeEvent('Signup: Requested', { method: 'modal' });
                notifications.showInfo(t('signupForm.emailSent'));
                onCancel();
            })
            .catch((error) => {
                notifications.showError(error.response?.data);
                setBusy(false);
            });
    };

    return (
        <form noValidate autoComplete="off" onSubmit={onSubmit} data-cy="signupForm">
            <AlTextField
                fullWidth
                autoFocus
                placeholder={t('placeholder.email')}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                error={touched && !isValid}
                helperText={touched && !isValid ? t('error.emailInvalid') : undefined}
                value={email}
                required={true}
                sx={{ mb: 2 }}
                disabled={busy}
                type="email"
                name="email"
                slotProps={{ htmlInput: { autoComplete: 'email' } }}
            />
            <FormActions
                onCancel={onCancel}
                submitLabel={t('signupForm.submit')}
                busy={busy}
                submitDisabled={!isValid}
            />
            <ConsentNote actionKey="signupForm.consentSignup" sx={{ mt: 2 }} />
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                {t('signupForm.haveAccountText')}{' '}
                <Link component="button" type="button" onClick={onLogin}>
                    {t('signupForm.haveAccountLink')}
                </Link>
            </Typography>
        </form>
    );
};

export { SignupForm };
